import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  DataTable,
  Button,
  TextField,
  Select,
  Text,
  Badge,
  Modal,
  Spinner,
  Banner,
  Box,
  InlineStack,
  BlockStack,
  Grid,
  Icon,
  Popover,
  ActionList
} from '@shopify/polaris';
import {
  ChartLineIcon,
  CalendarIcon,
  ClockIcon,
  CreditCardIcon,
  PhoneIcon,
  ChatIcon,
  ViewIcon,
  XIcon
} from '@shopify/polaris-icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { API_CONFIG } from '../config/api';
import axios from 'axios';

interface UsageLog {
  id: number;
  created_at: string;
  transaction_type: string;
  minutes_used: number;
  credits_used: number;
  description: string;
  call_log_id?: number;
  tokens_used?: number;
  prompts_count?: number;
  prompt_history?: PromptHistory[];
}

interface PromptHistory {
  id: number;
  prompt_model_name: string;
  prompt_type: string;
  tokens_used: number;
  credits_used: number;
  created_at: string;
  success: boolean;
}

interface UsageSummary {
  total_minutes_used: number;
  total_credits_used: number;
  total_calls: number;
  average_call_duration: number;
  total_tokens_used?: number;
  average_tokens_per_call?: number;
  total_prompts?: number;
  average_prompts_per_call?: number;
}

interface UsageData {
  summary: UsageSummary;
  date_range: {
    start_date: string;
    end_date: string;
  };
  usage_logs: UsageLog[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
    per_page: number;
  };
  chart_data?: ChartDataPoint[];
}

interface ChartDataPoint {
  date: string;
  tokens: number;
  credits: number;
  calls: number;
}

const Usage: React.FC = () => {
  const { user } = useAuth();
  const { selectedClient } = useClient();
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [selectedLog, setSelectedLog] = useState<UsageLog | null>(null);
  const [showLogDetails, setShowLogDetails] = useState(false);
  const [promptHistory, setPromptHistory] = useState<PromptHistory[]>([]);
  const [loadingPromptHistory, setLoadingPromptHistory] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Set default date range (last 30 days)
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  }, []);

  // Format date range for display
  const formatDateRange = () => {
    if (!startDate || !endDate) return 'Date Range';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };
    
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  // Load usage data
  const loadUsageData = async () => {
    // For admin users, use their own tenant; for support managers, require selectedClient
    if (!isAdmin && !selectedClient) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        page: currentPage.toString(),
        per_page: perPage.toString()
      });

      // Use selectedClient.id for support managers, user.tenant.id for admin users
      const tenantId = isAdmin ? user?.tenant?.id : selectedClient?.id;
      const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/support_clients/${tenantId}/usage?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success) {
        setUsageData(response.data.data);
      } else {
        setError(response.data.error || 'Failed to load usage data');
      }
    } catch (err: any) {
      console.error('Error loading usage data:', err);
      setError(err.response?.data?.error || 'An error occurred while loading usage data');
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts or dependencies change
  useEffect(() => {
    // For admin users, load data with their own tenant; for support managers, require selectedClient
    if ((isAdmin || selectedClient) && startDate && endDate) {
      loadUsageData();
    }
  }, [selectedClient, startDate, endDate, currentPage, perPage, isAdmin]);

  // Handle date filter changes
  const handleDateFilterChange = () => {
    setCurrentPage(1); // Reset to first page when changing date range
    setDatePickerOpen(false);
    loadUsageData();
  };

  // Handle date picker button click
  const handleDatePickerClick = () => {
    setDatePickerOpen(true);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle per page change
  const handlePerPageChange = (newPerPage: string) => {
    setPerPage(parseInt(newPerPage));
    setCurrentPage(1);
  };

  // Handle log details
  const handleLogClick = async (log: UsageLog) => {
    setSelectedLog(log);
    setShowLogDetails(true);
    
    // Load prompt history for this call
    if (log.call_log_id) {
      setLoadingPromptHistory(true);
      try {
        // Use selectedClient.id for support managers, user.tenant.id for admin users
        const tenantId = isAdmin ? user?.tenant?.id : selectedClient?.id;
        const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/support_clients/${tenantId}/call_logs/${log.call_log_id}/prompt_history`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.data.success) {
          setPromptHistory(response.data.data.prompt_history || []);
        }
      } catch (err) {
        console.error('Error loading prompt history:', err);
        setPromptHistory([]);
      } finally {
        setLoadingPromptHistory(false);
      }
    } else {
      setPromptHistory([]);
    }
  };

  // Close log details modal
  const handleCloseLogDetails = () => {
    setShowLogDetails(false);
    setSelectedLog(null);
    setPromptHistory([]);
  };

  // Format transaction type for display
  const formatTransactionType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format duration
  const formatDuration = (minutes: number) => {
    if (minutes < 1) {
      return `${Math.round(minutes * 60)}s`;
    }
    return `${minutes.toFixed(1)}m`;
  };

  // Get transaction type badge tone
  const getTransactionTypeBadge = (type: string) => {
    const colors: { [key: string]: 'success' | 'warning' | 'critical' | 'info' } = {
      'usage': 'info',
      'credit_addition': 'success',
      'credit_deduction': 'warning',
      'plan_change': 'critical',
      'refund': 'success'
    };
    return colors[type] || 'info';
  };

  // Check if user is support manager or admin
  const isSupportManager = user?.role === 'support_manager';
  const isAdminOrSupportManager = isAdmin || isSupportManager;

  // Show placeholder for support manager without tenant (admin users can see their own tenant data)
  if (isSupportManager && !selectedClient) {
    return (
      <Page title="Usage Analytics">
        <Card>
          <Text variant="headingMd" as="h2">
            Usage Analytics
          </Text>
          <Text variant="bodyMd" as="p">
            Please select a client to view their usage details.
          </Text>
        </Card>
      </Page>
    );
  }

  return (
    <Page 
      title="Usage Analytics" 
      fullWidth
    >
      {/* Date Range Picker Button - Top Right */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1 }}>
        <Button
          onClick={handleDatePickerClick}
          icon={CalendarIcon}
          variant="secondary"
        >
          {formatDateRange()}
        </Button>
      </div>
      
      <BlockStack gap="400">
        {/* Usage Summary Cards */}
        {usageData && (
          <div style={{
            display: 'flex',
            width: '100%',
            gap: '16px',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
            alignItems: 'stretch'
          }}>
            <div style={{
              flex: '1 1 0',
              minWidth: '0',
              width: '100%'
            }}>
              <Card>
                <div style={{ height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                    <Icon source={ClockIcon} />
                    <Text variant="bodyMd" tone="subdued" as="p">
                      Total Minutes
                    </Text>
                  </div>
                  <Text variant="headingLg" as="p">
                    {formatDuration(Number(usageData.summary.total_minutes_used) || 0)}
                  </Text>
                </div>
              </Card>
            </div>
            
            <div style={{
              flex: '1 1 0',
              minWidth: '0',
              width: '100%'
            }}>
              <Card>
                <div style={{ height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                    <Icon source={CreditCardIcon} />
                    <Text variant="bodyMd" tone="subdued" as="p">
                      Total Credits
                    </Text>
                  </div>
                  <Text variant="headingLg" as="p">
                    ₹{(Number(usageData.summary.total_credits_used) || 0).toFixed(2)}
                  </Text>
                </div>
              </Card>
            </div>
            
            <div style={{
              flex: '1 1 0',
              minWidth: '0',
              width: '100%'
            }}>
              <Card>
                <div style={{ height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                    <Icon source={PhoneIcon} />
                    <Text variant="bodyMd" tone="subdued" as="p">
                      Total Calls
                    </Text>
                  </div>
                  <Text variant="headingLg" as="p">
                    {Number(usageData.summary.total_calls) || 0}
                  </Text>
                </div>
              </Card>
            </div>
            
            <div style={{
              flex: '1 1 0',
              minWidth: '0',
              width: '100%'
            }}>
              <Card>
                <div style={{ height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                    <Icon source={ChatIcon} />
                    <Text variant="bodyMd" tone="subdued" as="p">
                      Total Prompts
                    </Text>
                  </div>
                  <Text variant="headingLg" as="p">
                    {Number(usageData.summary.total_prompts) || 0}
                  </Text>
                </div>
              </Card>
            </div>
            
            {isSupportManager && !isAdmin && usageData.summary.total_tokens_used !== undefined && (
              <div style={{
                flex: '1 1 0',
                minWidth: '0',
                width: '100%'
              }}>
                <Card>
                  <div style={{ height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                      <Icon source={ChartLineIcon} />
                      <Text variant="bodyMd" tone="subdued" as="p">
                        Total Tokens
                      </Text>
                    </div>
                    <Text variant="headingLg" as="p">
                      {(Number(usageData.summary.total_tokens_used) || 0).toLocaleString()}
                    </Text>
                  </div>
                </Card>
              </div>
            )}
            
            <div style={{
              flex: '1 1 0',
              minWidth: '0',
              width: '100%'
            }}>
              <Card>
                <div style={{ height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                    <Icon source={ClockIcon} />
                    <Text variant="bodyMd" tone="subdued" as="p">
                      Avg Duration
                    </Text>
                  </div>
                  <Text variant="headingLg" as="p">
                    {formatDuration(Number(usageData.summary.average_call_duration) || 0)}
                  </Text>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Charts Section */}
        {usageData && usageData.chart_data && usageData.chart_data.length > 0 && (
          <div style={{
            display: 'flex',
            width: '100%',
            gap: '16px',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
            alignItems: 'stretch'
          }}>
            {/* Token Usage Chart - Only show for support managers, not admin users */}
            {isSupportManager && !isAdmin && (
              <div style={{
                flex: '1 1 0',
                minWidth: '0',
                width: '100%'
              }}>
                <Card>
                  <BlockStack gap="300">                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <Icon source={ChartLineIcon} />
                      </div>
                      <Text variant="headingMd" as="h2">
                        Token Usage Over Time
                      </Text>
                    </div>
                    <div style={{ height: '300px', width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={usageData.chart_data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Line type="monotone" dataKey="tokens" stroke="#8884d8" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </BlockStack>
                </Card>
              </div>
            )}
            
            <div style={{
              flex: '1 1 0',
              minWidth: '0',
              width: '100%'
            }}>
              <Card>
                <BlockStack gap="300">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <Icon source={CreditCardIcon} />
                    </div>
                    <Text variant="headingMd" as="h2">
                      Credit Usage Over Time
                    </Text>
                  </div>
                  <div style={{ height: '300px', width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={usageData.chart_data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="credits" fill="#82ca9d" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </BlockStack>
              </Card>
            </div>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <Banner tone="critical">
            <Text variant="bodyMd" as="p">
              {error}
            </Text>
          </Banner>
        )}

        {/* Usage Logs Card */}
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between">
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Icon source={ViewIcon} />
                <Text variant="headingMd" as="h2">
                  Usage Logs
                </Text>
              </div>
              {usageData && (
                <InlineStack gap="200">
                  <Text variant="bodyMd" tone="subdued" as="p">
                    {usageData.pagination.total_count} total entries
                  </Text>
                  <Text variant="bodyMd" tone="subdued" as="p">
                    • Click any row for details
                  </Text>
                </InlineStack>
              )}
            </InlineStack>

            {loading ? (
              <InlineStack align="center">
                <Spinner size="small" />
                <Text variant="bodyMd" as="p">
                  Loading usage logs...
                </Text>
              </InlineStack>
            ) : usageData ? (
              <BlockStack gap="200">
                {/* Data Table */}
                <div style={{ cursor: 'pointer' }}>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                    headings={[
                      'Date & Time',
                      'Type',
                      'Minutes',
                      'Credits',
                      'Prompts',
                      ...(isSupportManager && !isAdmin ? ['Tokens'] : [])
                    ]}
                    rows={usageData.usage_logs.map((log, index) => [
                      <div 
                        key={`date-${index}`}
                        onClick={() => handleLogClick(log)}
                        style={{ cursor: 'pointer', padding: '8px 0' }}
                      >
                        {formatDate(log.created_at)}
                      </div>,
                      <div 
                        key={`type-${index}`}
                        onClick={() => handleLogClick(log)}
                        style={{ cursor: 'pointer', padding: '8px 0' }}
                      >
                        <Badge tone={getTransactionTypeBadge(log.transaction_type)}>
                          {formatTransactionType(log.transaction_type)}
                        </Badge>
                      </div>,
                      <div 
                        key={`minutes-${index}`}
                        onClick={() => handleLogClick(log)}
                        style={{ cursor: 'pointer', padding: '8px 0' }}
                      >
                        {formatDuration(Number(log.minutes_used) || 0)}
                      </div>,
                      <div 
                        key={`credits-${index}`}
                        onClick={() => handleLogClick(log)}
                        style={{ cursor: 'pointer', padding: '8px 0' }}
                      >
                        ₹{(Number(log.credits_used) || 0).toFixed(2)}
                      </div>,
                      <div 
                        key={`prompts-${index}`}
                        onClick={() => handleLogClick(log)}
                        style={{ cursor: 'pointer', padding: '8px 0' }}
                      >
                        {log.prompts_count || 0}
                      </div>,
                      ...(isSupportManager && !isAdmin ? [
                        <div 
                          key={`tokens-${index}`}
                          onClick={() => handleLogClick(log)}
                          style={{ cursor: 'pointer', padding: '8px 0' }}
                        >
                          {log.tokens_used ? (Number(log.tokens_used) || 0).toLocaleString() : 'N/A'}
                        </div>
                      ] : [])
                    ])}
                  />
                </div>

                {/* Pagination */}
                {usageData.pagination.total_pages > 1 && (
                  <InlineStack align="space-between">
                    <InlineStack gap="200">
                      <Text variant="bodyMd" as="p">
                        Show
                      </Text>
                      <Select
                        label="Entries per page"
                        labelHidden
                        options={[
                          { label: '10', value: '10' },
                          { label: '20', value: '20' },
                          { label: '50', value: '50' },
                          { label: '100', value: '100' }
                        ]}
                        value={perPage.toString()}
                        onChange={handlePerPageChange}
                      />
                      <Text variant="bodyMd" as="p">
                        entries per page
                      </Text>
                    </InlineStack>

                    <InlineStack gap="200">
                      <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Text variant="bodyMd" as="p">
                        Page {currentPage} of {usageData.pagination.total_pages}
                      </Text>
                      <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === usageData.pagination.total_pages}
                      >
                        Next
                      </Button>
                    </InlineStack>
                  </InlineStack>
                )}
              </BlockStack>
            ) : (
              <Text variant="bodyMd" tone="subdued" as="p">
                No usage data available for the selected date range.
              </Text>
            )}
          </BlockStack>
        </Card>
        <div style={{ paddingBottom: '24px' }}></div>
      </BlockStack>

      {/* Date Range Picker Popover */}
      <Popover
        active={datePickerOpen}
        activator={
          <div 
            id="date-picker-activator"
            style={{ 
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '1px',
              height: '1px',
              opacity: 0,
              pointerEvents: 'none'
            }}
          />
        }
        onClose={() => setDatePickerOpen(false)}
        preferredAlignment="right"
        preferredPosition="below"
      >
          <Box padding="400">
            <BlockStack gap="300">
              <Text variant="headingMd" as="h3">
                Select Date Range
              </Text>
              <InlineStack gap="300">
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={setStartDate}
                  autoComplete="off"
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={setEndDate}
                  autoComplete="off"
                />
              </InlineStack>
              <InlineStack align="end" gap="200">
                <Button onClick={() => setDatePickerOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleDateFilterChange}>
                  Apply
                </Button>
              </InlineStack>
            </BlockStack>
          </Box>
        </Popover>

      {/* Enhanced Log Details Modal */}
      <Modal
        open={showLogDetails}
        onClose={handleCloseLogDetails}
        title="Usage Log Details"
        size="large"
        primaryAction={{
          content: 'Close',
          onAction: handleCloseLogDetails,
          icon: XIcon
        }}
      >
        <Modal.Section>
          {selectedLog && (
            <BlockStack gap="400">
              {/* Basic Information */}
              <Card>
                <BlockStack gap="300">
                  <Text variant="headingMd" as="h3">
                    Call Information
                  </Text>
                  <Grid>
                    <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                      <BlockStack gap="100">
                        <Text variant="bodyMd" as="p" fontWeight="semibold">
                          Date & Time
                        </Text>
                        <Text variant="bodyMd" as="p">
                          {formatDate(selectedLog.created_at)}
                        </Text>
                      </BlockStack>
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                      <BlockStack gap="100">
                        <Text variant="bodyMd" as="p" fontWeight="semibold">
                          Transaction Type
                        </Text>
                        <Badge tone={getTransactionTypeBadge(selectedLog.transaction_type)}>
                          {formatTransactionType(selectedLog.transaction_type)}
                        </Badge>
                      </BlockStack>
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                      <BlockStack gap="100">
                        <Text variant="bodyMd" as="p" fontWeight="semibold">
                          Minutes Used
                        </Text>
                        <Text variant="bodyMd" as="p">
                          {formatDuration(Number(selectedLog.minutes_used) || 0)}
                        </Text>
                      </BlockStack>
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                      <BlockStack gap="100">
                        <Text variant="bodyMd" as="p" fontWeight="semibold">
                          Credits Used
                        </Text>
                        <Text variant="bodyMd" as="p">
                          ₹{(Number(selectedLog.credits_used) || 0).toFixed(2)}
                        </Text>
                      </BlockStack>
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                      <BlockStack gap="100">
                        <Text variant="bodyMd" as="p" fontWeight="semibold">
                          Prompts Count
                        </Text>
                        <Text variant="bodyMd" as="p">
                          {selectedLog.prompts_count || 0}
                        </Text>
                      </BlockStack>
                    </Grid.Cell>
                    {isSupportManager && !isAdmin && (
                      <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                        <BlockStack gap="100">
                          <Text variant="bodyMd" as="p" fontWeight="semibold">
                            Tokens Used
                          </Text>
                          <Text variant="bodyMd" as="p">
                            {selectedLog.tokens_used ? (Number(selectedLog.tokens_used) || 0).toLocaleString() : 'N/A'}
                          </Text>
                        </BlockStack>
                      </Grid.Cell>
                    )}
                    {selectedLog.call_log_id && (
                      <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                        <BlockStack gap="100">
                          <Text variant="bodyMd" as="p" fontWeight="semibold">
                            Call Log ID
                          </Text>
                          <Text variant="bodyMd" as="p">
                            {selectedLog.call_log_id}
                          </Text>
                        </BlockStack>
                      </Grid.Cell>
                    )}
                  </Grid>
                  
                  {selectedLog.description && (
                    <BlockStack gap="100">
                      <Text variant="bodyMd" as="p" fontWeight="semibold">
                        Description
                      </Text>
                      <Text variant="bodyMd" as="p">
                        {selectedLog.description}
                      </Text>
                    </BlockStack>
                  )}
                </BlockStack>
              </Card>

              {/* Prompt History */}
              {selectedLog.call_log_id && (
                <Card>
                  <BlockStack gap="300">
                    <InlineStack align="space-between">
                      <Text variant="headingMd" as="h3">
                        Prompt History
                      </Text>
                      {loadingPromptHistory && <Spinner size="small" />}
                    </InlineStack>
                    
                    {promptHistory.length > 0 ? (
                      <DataTable
                        columnContentTypes={isSupportManager && !isAdmin ? ['text', 'text', 'text', 'text', 'text', 'text'] : ['text', 'text', 'text', 'text']}
                        headings={isSupportManager && !isAdmin ? ['Type', 'Model Name', 'Tokens', 'Credits', 'Status', 'Time'] : ['Type', 'Model Name', 'Status', 'Time']}
                        rows={promptHistory.map((prompt, index) => {
                          const baseRow = [
                            prompt.prompt_type || 'unknown', // Show prompt type in first column
                            prompt.prompt_model_name, // Show prompt model name in the "Model Name" column
                            <Badge tone={prompt.success ? 'success' : 'critical'}>
                              {prompt.success ? 'Success' : 'Failed'}
                            </Badge>,
                            formatDate(prompt.created_at)
                          ];
                          
                          // Insert tokens and credits columns for support managers only
                          if (isSupportManager && !isAdmin) {
                            baseRow.splice(2, 0, (Number(prompt.tokens_used) || 0).toLocaleString());
                            baseRow.splice(3, 0, `₹${(Number(prompt.credits_used) || 0).toFixed(2)}`);
                          }
                          
                          return baseRow;
                        })}
                      />
                    ) : !loadingPromptHistory ? (
                      <Text variant="bodyMd" tone="subdued" as="p">
                        No prompt history available for this call.
                      </Text>
                    ) : null}
                  </BlockStack>
                </Card>
              )}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default Usage;